"use client";

import { c as _c } from "react-compiler-runtime";
import { ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { Button, Text } from "@/components/primitives";
import { useAnalytics } from "@/core/analytics";
import { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useTranslation } from "@/core/i18n";
import { useI18n } from "@/core/i18n/useI18n/useI18n";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { usePlatformDataPrivacyRegion } from "@/features/platform/usePlatformDataPrivacyRegion";
import { deriveConsentLanguageCode, getBiometricConsentCheckboxPrefixKey, getBiometricConsentItemsForRegion } from "@/features/assessments/consent/regionalConsent";
import { ConsentOverlay } from "../components/ConsentOverlay";
import { useConsentGate } from "../hooks/useConsentGate";
import { captureAssessmentConsents } from "@/features/assessments/consent/consentUtils";
type ConsentStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onAccepted: () => void;
  onDeclined: () => void;
};
export function ConsentStage(t0) {
  const $ = _c(55);
  const {
    onAccepted,
    assessment,
    headers
  } = t0;
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType,
    subdomainSlug
  } = useAssessmentStep();
  const {
    currentLang
  } = useI18n();
  const {
    dataPrivacyRegion,
    isPending: isRegionPending
  } = usePlatformDataPrivacyRegion(subdomainSlug);
  const [showDeclinedView, setShowDeclinedView] = useState(false);
  let t1;
  bb0: {
    if (!dataPrivacyRegion) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== dataPrivacyRegion || $[1] !== t) {
      t2 = getBiometricConsentItemsForRegion(dataPrivacyRegion, t);
      $[0] = dataPrivacyRegion;
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const biometricConsentItems = t1;
  let t2;
  if ($[3] !== dataPrivacyRegion) {
    t2 = dataPrivacyRegion ? {
      checkboxPrefix: getBiometricConsentCheckboxPrefixKey(dataPrivacyRegion)
    } : undefined;
    $[3] = dataPrivacyRegion;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const consentTranslationKeys = t2;
  let t3;
  let t4;
  if ($[5] !== analytics || $[6] !== flowType) {
    t3 = () => {
      analytics.trackEvent("assessment_data_capture_consent_declined", {
        flowType
      });
      setShowDeclinedView(true);
    };
    t4 = () => {
      analytics.trackEvent("assessment_data_capture_consent_given", {
        flowType
      });
    };
    $[5] = analytics;
    $[6] = flowType;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== assessment || $[10] !== currentLang || $[11] !== dataPrivacyRegion || $[12] !== flowType || $[13] !== headers || $[14] !== onAccepted) {
    t5 = async () => {
      if (!dataPrivacyRegion) {
        return;
      }
      const languageCode = deriveConsentLanguageCode(currentLang);
      await captureAssessmentConsents(assessment.id, flowType, languageCode, dataPrivacyRegion, {
        headers
      });
      onAccepted();
    };
    $[9] = assessment;
    $[10] = currentLang;
    $[11] = dataPrivacyRegion;
    $[12] = flowType;
    $[13] = headers;
    $[14] = onAccepted;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      onCancel: t3,
      trackConsent: t4,
      onConsentAccepted: t5
    };
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const {
    consentChecked,
    setConsentChecked,
    isVerifying,
    handleConsentAgree,
    handleConsentCancel
  } = useConsentGate(t6);
  let t7;
  if ($[20] !== setConsentChecked) {
    t7 = () => {
      setConsentChecked(false);
      setShowDeclinedView(false);
    };
    $[20] = setConsentChecked;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleGrantPermission = t7;
  if (showDeclinedView) {
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-full"><ShieldCheck className="text-muted-foreground h-8 w-8" /></div>;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== t) {
      t9 = t("assessmentSteps.studioSession.consent.declinedTitle");
      $[23] = t;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t9) {
      t10 = <Text component="h1" text={t9} tone="white" variant="heading-xl" />;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t) {
      t11 = t("assessmentSteps.studioSession.consent.declinedMessage");
      $[27] = t;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11) {
      t12 = <Text component="p" text={t11} tone="white" variant="body-sm" />;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== t10 || $[32] !== t12) {
      t13 = <div className="flex flex-col gap-2">{t10}{t12}</div>;
      $[31] = t10;
      $[32] = t12;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== t) {
      t14 = t("assessmentSteps.studioSession.consent.grantPermission");
      $[34] = t;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== t14) {
      t15 = <Text component="span" text={t14} />;
      $[36] = t14;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    let t16;
    if ($[38] !== handleGrantPermission || $[39] !== t15) {
      t16 = <Button size="lg" type="button" onClick={handleGrantPermission}>{t15}</Button>;
      $[38] = handleGrantPermission;
      $[39] = t15;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t13 || $[42] !== t16) {
      t17 = <div className="flex min-h-[calc(100dvh-128px)] flex-col items-center justify-center px-6 [padding-bottom:calc(env(safe-area-inset-bottom)+1.5rem)] text-center"><div className="flex w-full max-w-sm flex-col items-center gap-8">{t8}{t13}{t16}</div></div>;
      $[41] = t13;
      $[42] = t16;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    return t17;
  }
  const t8 = isVerifying || isRegionPending || !dataPrivacyRegion;
  let t9;
  if ($[44] !== handleConsentCancel) {
    t9 = open => {
      if (!open) {
        handleConsentCancel();
      }
    };
    $[44] = handleConsentCancel;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== biometricConsentItems || $[47] !== consentChecked || $[48] !== consentTranslationKeys || $[49] !== handleConsentAgree || $[50] !== handleConsentCancel || $[51] !== setConsentChecked || $[52] !== t8 || $[53] !== t9) {
    t10 = <ConsentOverlay open={true} consentChecked={consentChecked} isVerifying={t8} biometricConsentItems={biometricConsentItems} translationKeys={consentTranslationKeys} onCancel={handleConsentCancel} onConfirm={handleConsentAgree} onConsentChange={setConsentChecked} onOpenChange={t9} />;
    $[46] = biometricConsentItems;
    $[47] = consentChecked;
    $[48] = consentTranslationKeys;
    $[49] = handleConsentAgree;
    $[50] = handleConsentCancel;
    $[51] = setConsentChecked;
    $[52] = t8;
    $[53] = t9;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  return t10;
}